/**
 * Page carte — partage « défi », bandeau du visiteur et classement « Mon
 * cercle » (migration 0285, lot E). Logique et textes dans `@/lib/cards/challenge`.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Check, Share2, Swords, Trophy } from 'lucide-react';
import { AvatarCircle, cn } from '@jeraisonne/ui';
import { buildCardQrUrl } from '@jeraisonne/db/cards';
import { useAuth } from '@/lib/auth';
import { shareContent } from '@/lib/native-share';
import { markChallengeOpened } from '@/lib/analytics/track';
import { cardLinkOrigin, challengeLinkOrigin } from '@/lib/cards/link-origins';
import {
  CIRCLE_RELATION_LABELS,
  acceptCardShare,
  buildChallengeUrl,
  challengeBannerText,
  challengeRelationText,
  challengeShareText,
  myRowOutsideTop,
  useCardShare,
  useCircleLeaderboard,
  buildCardPageUrl,
  useCardSlug,
  useCreateCardShare,
  warmCardPreview,
  warmChallengePreview,
  type ChallengeRelation,
  type CircleEntry,
} from '@/lib/cards/challenge';

type ShareStatus = 'idle' | 'copied' | 'error';

export const ROUND_BUTTON =
  'border-ink-200 text-primary-600 hover:border-primary-300 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition active:scale-95 disabled:opacity-60';

/** Bouton rond + message d'erreur ancré dessous, partagé par « Partager » et « Défier ». */
function RoundShareButton({
  label,
  title,
  icon,
  pending,
  run,
  wideLabel,
}: {
  label: string;
  title: string;
  icon: ReactNode;
  pending?: boolean;
  run: () => Promise<SharePayload | null>;
  /** Variante pleine largeur avec libellé visible (ex. « Mon cercle » vide). */
  wideLabel?: string;
}) {
  const [status, setStatus] = useState<ShareStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const onClick = async () => {
    setError(null);
    let payload: SharePayload | null;
    try {
      payload = await run();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus('error');
      return;
    }
    if (!payload) return;
    const result = await shareContent(payload);
    if (result === 'copied') {
      setStatus('copied');
      setTimeout(() => setStatus('idle'), 2200);
    } else if (result === 'failed') {
      setError('Partage indisponible sur cet appareil.');
      setStatus('error');
    }
  };

  if (wideLabel) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => void onClick()}
          disabled={pending}
          className="border-primary-200 text-primary-700 hover:bg-primary-50 ocean:bg-white/5 flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98] disabled:opacity-60"
        >
          {status === 'copied' ? <Check className="h-4 w-4" aria-hidden /> : icon}
          {status === 'copied' ? 'Lien copié : envoie-le à un proche' : wideLabel}
        </button>
        {error && (
          <p role="alert" className="text-accent-600 text-xs">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => void onClick()}
        disabled={pending}
        aria-label={status === 'copied' ? 'Lien copié' : label}
        title={title}
        className={cn(ROUND_BUTTON, status === 'copied' && 'text-green-600')}
      >
        {status === 'copied' ? <Check className="h-5 w-5" /> : icon}
      </button>
      {error && (
        <p
          role="alert"
          className="text-accent-600 absolute right-0 top-12 z-10 w-56 rounded-lg bg-white p-2 text-right text-xs shadow"
        >
          {error}
        </p>
      )}
    </div>
  );
}

type SharePayload = { title: string; text: string; url: string };

/**
 * Partage simple de la carte — visible pour tous. Partage la fiche SEO du site
 * (`/skillcards/{slug}`, avec image d'aperçu) dont l'image est pré-générée dès
 * le clic ; à défaut de slug (rare), le lien court de l'app.
 */
export function CardShareButton({
  cardId,
  shortCode,
  title,
}: {
  cardId: string;
  shortCode: string;
  title: string;
}) {
  const slug = useCardSlug(cardId);
  return (
    <RoundShareButton
      label="Partager cette carte"
      title="Partager"
      icon={<Share2 className="h-5 w-5" />}
      run={async () => {
        let url = buildCardQrUrl(shortCode, cardLinkOrigin());
        if (slug.data) {
          url = buildCardPageUrl(slug.data, challengeLinkOrigin());
          warmCardPreview(cardId, url);
        }
        return {
          title: `SKILLCARD « ${title} »`,
          text: `Découvre la SKILLCARD « ${title} » sur JeRaisonne !`,
          url,
        };
      }}
    />
  );
}

/**
 * Bouton « défi » (épées) — UNIQUEMENT pour un compte connecté (non anonyme) :
 * fige ma maîtrise et partage le lien du défi (image OG).
 */
export function CardChallengeShareButton({
  cardId,
  title,
  wideLabel,
}: {
  cardId: string;
  title: string;
  /** Bouton pleine largeur avec ce libellé (sinon : bouton rond « épées »). */
  wideLabel?: string;
}) {
  const { session, profile } = useAuth();
  const create = useCreateCardShare(cardId);
  const canChallenge = !!session && !!profile && !session.user?.is_anonymous;
  if (!canChallenge) return null;

  return (
    <RoundShareButton
      label="Défier un ami sur cette carte"
      title="Défier un ami"
      icon={<Swords className={wideLabel ? 'h-4 w-4' : 'h-5 w-5'} aria-hidden />}
      pending={create.isPending}
      wideLabel={wideLabel}
      run={async () => {
        const share = await create.mutateAsync();
        const url = buildChallengeUrl(share.token, challengeLinkOrigin());
        warmChallengePreview(share.token, url);
        return {
          title: `Défi SKILLCARD « ${title} »`,
          text: challengeShareText(share, title),
          url,
        };
      }}
    />
  );
}

/** Bandeau « Léa te défie : 80 % de maîtrise » ; prévient le partageur une fois connecté. */
export function CardChallengeBanner({ token, cardId }: { token: string; cardId: string }) {
  const share = useCardShare(token);
  const { session, profile } = useAuth();
  const data = share.data && share.data.card_id === cardId ? share.data : null;
  const [relation, setRelation] = useState<ChallengeRelation>(null);

  // Relever le défi propose aussi de se relier (camarades, prof-élève) :
  // demande en attente, acceptée ou refusée par celui qui a partagé.
  useEffect(() => {
    if (!data || !session || !profile || session.user?.is_anonymous) return;
    let cancelled = false;
    void acceptCardShare(token).then((r) => {
      if (!cancelled) setRelation(r.relation);
    });
    return () => {
      cancelled = true;
    };
  }, [data, session, profile, token]);

  // Défi reçu ouvert : compté une fois (tunnel « défis » des statistiques).
  const opened = data != null;
  useEffect(() => {
    if (opened) markChallengeOpened('skillcard');
  }, [opened, token]);

  const relationText = data ? challengeRelationText(relation, data.first_name) : null;

  if (!data) return null;
  return (
    <div
      className="from-primary-600 to-primary-500 flex items-center gap-3 rounded-2xl bg-gradient-to-r px-4 py-3 text-white shadow-sm"
      data-testid="card-challenge-banner"
    >
      <Swords className="h-6 w-6 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-display text-base font-bold">{challengeBannerText(data)}</p>
        <p className="text-sm text-white/85">
          {data.mastery == null
            ? 'Retourne la carte et lance-toi.'
            : `${data.hora ?? 0} HORA. Travaille les exercices de la carte pour faire mieux !`}
        </p>
        {relationText && (
          <p className="mt-1 text-sm font-semibold text-white" role="status">
            {relationText}
          </p>
        )}
      </div>
    </div>
  );
}

function CircleRow({ entry }: { entry: CircleEntry }) {
  return (
    <li
      className={cn(
        'flex min-h-[2.75rem] items-center gap-3 rounded-lg px-2',
        entry.is_me && 'bg-primary-50',
      )}
    >
      <span className="text-ink-500 w-6 shrink-0 text-right text-sm font-bold tabular-nums">
        {entry.rank}
      </span>
      <AvatarCircle name={entry.first_name} src={entry.avatar_url} size="sm" />
      <span className="min-w-0 flex-1 truncate text-sm">
        <span className="text-ink-900 font-semibold">{entry.is_me ? 'Toi' : entry.first_name}</span>
        {!entry.is_me && (
          <span className="text-ink-500"> · {CIRCLE_RELATION_LABELS[entry.relation]}</span>
        )}
      </span>
      <span className="text-ink-500 shrink-0 text-xs tabular-nums">{entry.mastery} %</span>
      <span className="text-ink-900 shrink-0 whitespace-nowrap text-right text-sm font-bold tabular-nums">
        {entry.hora.toLocaleString('fr-FR')} <span className="text-ink-500 text-xs">HORA</span>
      </span>
    </li>
  );
}

/** « Mon cercle » : profs, parents, élèves, camarades qui ont la carte (top 10 + ma place). */
export function CardCircleBoard({ cardId, cardTitle }: { cardId: string; cardTitle?: string }) {
  const { session, profile } = useAuth();
  const enabled = !!session && !!profile && !session.user?.is_anonymous;
  const board = useCircleLeaderboard(cardId, enabled);
  if (!enabled || !board.data) return null;
  const others = board.data.entries.filter((e) => !e.is_me).length;
  const extra = myRowOutsideTop(board.data);

  return (
    <section
      className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4"
      aria-labelledby="card-circle-title"
    >
      <h2
        id="card-circle-title"
        className="text-ink-900 font-display flex items-center gap-2 text-base font-bold"
      >
        <Trophy className="text-primary-500 h-5 w-5" aria-hidden />
        Mon cercle
      </h2>
      {others === 0 ? (
        <div className="space-y-2">
          <p className="text-ink-600 text-sm">
            Aucun de tes proches n’a encore cette carte. Défie un camarade : il verra ta maîtrise et
            tentera de faire mieux.
          </p>
          <CardChallengeShareButton
            cardId={cardId}
            title={cardTitle ?? 'cette SKILLCARD'}
            wideLabel="Défier un proche sur cette carte"
          />
        </div>
      ) : (
        <ol className="space-y-1">
          {board.data.entries.map((e) => (
            <CircleRow key={`${e.rank}-${e.first_name}-${e.relation}`} entry={e} />
          ))}
          {extra && (
            <>
              <li aria-hidden className="text-ink-300 px-2 text-center text-xs">
                …
              </li>
              <CircleRow entry={extra} />
            </>
          )}
        </ol>
      )}
      {profile?.role === 'student' && !board.data.my_visibility && (
        <p className="text-ink-500 text-xs">
          Tes camarades ne voient pas ta progression. Tu peux l’activer dans ton profil.
        </p>
      )}
    </section>
  );
}
